import React, { useEffect, useRef, useState } from "react";
import { useJsApiLoader } from "@react-google-maps/api";

const LIBRARIES = ["places"];
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/**
 * GooglePlacesAutocomplete — thin wrapper around the real Google Places
 * Autocomplete widget (not the OSM/Nominatim-based AddressAutocomplete used
 * elsewhere) so CPD dispatch addresses are verified against Google's index
 * before being handed to a TP for driver selection.
 */
export default function GooglePlacesAutocomplete({ value, onChange, placeholder, testId }) {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: API_KEY, libraries: LIBRARIES });
  const inputRef = useRef(null);
  const acRef = useRef(null);
  const [text, setText] = useState(value || "");

  useEffect(() => { setText(value || ""); }, [value]);

  useEffect(() => {
    if (!isLoaded || !inputRef.current || acRef.current) return;
    acRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: "us" },
      fields: ["formatted_address", "geometry", "place_id"],
    });
    acRef.current.addListener("place_changed", () => {
      const place = acRef.current.getPlace();
      const addr = place?.formatted_address || inputRef.current.value;
      setText(addr);
      onChange({
        address: addr,
        lat: place?.geometry?.location?.lat?.(),
        lng: place?.geometry?.location?.lng?.(),
        place_id: place?.place_id,
        verified: !!place?.place_id,
      });
    });
  }, [isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <input
      ref={inputRef}
      type="text"
      value={text}
      placeholder={placeholder || "Start typing an address…"}
      onChange={(e) => { setText(e.target.value); onChange({ address: e.target.value, verified: false }); }}
      data-testid={testId}
      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest transition"
      autoComplete="off"
    />
  );
}
